import React, { useEffect, useRef, useState, useCallback } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLocation } from 'react-router-dom';
import useReducedMotion from '../hooks/useReducedMotion';
import useAmbientAudio from '../hooks/useAmbientAudio';
import Preloader from '../components/Preloader';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Services from '../components/Services';
import Values from '../components/Values';
import Portfolio from '../components/Portfolio';
import Testimonials from '../components/Testimonials';
import Process from '../components/Process';
import Contact from '../components/Contact';
import Footer from '../components/Footer';

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
  const reduced = useReducedMotion();
  const location = useLocation();
  const [loaded, setLoaded] = useState(false);
  const { muted, toggle: toggleSound, play: playAmbient } = useAmbientAudio();
  const lenisRef = useRef(null);

  useEffect(() => {
    if (reduced) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;

    // Sync Lenis with GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);
    const ticker = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(ticker);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [reduced]);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(el, { offset: id === 'hero' ? 0 : -20 });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Scroll to a section after navigating home from a legal page
  useEffect(() => {
    if (!loaded) return;
    const goto = location.state?.goto;
    if (goto) {
      const t = setTimeout(() => scrollTo(goto), 300);
      return () => clearTimeout(t);
    }
  }, [loaded, location.state, scrollTo]);

  const toggleSoundHandler = useCallback(() => toggleSound(), [toggleSound]);

  // Auto-start soothing ambient audio on the first user gesture (browsers block
  // true autoplay). Respects prefers-reduced-motion.
  useEffect(() => {
    if (!loaded || reduced) return;
    let started = false;
    const startOnce = () => {
      if (started) return;
      started = true;
      playAmbient();
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener('pointerdown', startOnce);
      window.removeEventListener('keydown', startOnce);
      window.removeEventListener('wheel', startOnce);
      window.removeEventListener('touchstart', startOnce);
    };
    window.addEventListener('pointerdown', startOnce, { once: false });
    window.addEventListener('keydown', startOnce);
    window.addEventListener('wheel', startOnce, { passive: true });
    window.addEventListener('touchstart', startOnce, { passive: true });
    return cleanup;
  }, [loaded, reduced, playAmbient]);

  return (
    <div className="relative bg-bone">
      <Preloader onDone={() => setLoaded(true)} />

      {loaded && (
        <>
          <Header onNav={scrollTo} muted={muted} onToggleSound={toggleSoundHandler} />
          <main>
            <Hero onNav={scrollTo} reduced={reduced} />
            <Services reduced={reduced} />
            <Values reduced={reduced} />
            <Portfolio />
            <Testimonials />
            <Process />
            <Contact />
          </main>
          <Footer onNav={scrollTo} />
        </>
      )}
    </div>
  );
}
